import { clickTab, setReactInputValue, setReactSelectValue } from '../../shared/domHelpers'
import { demoRoutes, setDemoTourResume, DEMO_IMEI } from '../DemoFlow'
import demoStore from '../mock/DemoStore'
import { DEMO_IDS } from '../mock/fixtures/mqttPacket'
import { unlockDemoSaveButton, lockDemoSaveButton, withNativeDemoSaveAllowed } from '../demoSaveControls'
import { skipStageConfig } from '../demoSkipStage'

const delay = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds))
const SUCCESS_MODAL_READING_TIME = 1400

const VARIAVEIS_PANEL_SELECTOR = '#container-campo-customizado.campo-customizado-visible'
const LISTA_READY = '[data-tour="grid-table"], [data-tour="grid-novo-btn"], [data-tour="help-equipamento-grid"]'
const DEMO_PARAM_MQTT_INDEX = 1

const getEquipamentoImeiLink = () => {
    const links = Array.from(document.querySelectorAll('[data-tour="equipamento-lista-imei"]'))
    const byImei = links.find((link) => (link.textContent || '').includes(DEMO_IMEI))
    if (byImei) return byImei
    if (links.length) return links[0]

    const byId = document.querySelector(
        `[data-tour="grid-table"] a[href*="/equipamento/${DEMO_IDS.equipamento}/editar"]`
    )
    if (byId) return byId

    return Array.from(document.querySelectorAll('[data-tour="grid-table"] tbody a')).find((link) =>
        (link.textContent || '').includes(DEMO_IMEI)
    )
}

const bindMinimizeOnClick = (element, handler) => {
    if (!element || element._hdvTourMinimizeClick) return

    element.addEventListener('click', handler)
    element._hdvTourMinimizeClick = handler
}

const unbindMinimizeOnClick = (element) => {
    if (!element?._hdvTourMinimizeClick) return

    element.removeEventListener('click', element._hdvTourMinimizeClick)
    delete element._hdvTourMinimizeClick
}

const focusFormulaInput = () => {
    const editor = document.querySelector('#formula-input-group .formula-editor')
    editor?.focus()
    editor?.dispatchEvent(new FocusEvent('focus', { bubbles: true }))
}

const openVariaveisPanel = (tourManager) => {
    focusFormulaInput()
    return tourManager.waitForElement(VARIAVEIS_PANEL_SELECTOR)
}

const ensureEquipamentoEdit = (tourManager) => {
    const openProtocolo = () => {
        clickTab('[data-tour="tab-protocolo"]')
        return delay(80).then(() =>
            tourManager.waitForElement('[data-tour="mqtt-topico-publicar"], [data-tour="mqtt-usuario"]')
        )
    }

    if (document.querySelector('[data-tour="tab-protocolo"], [data-tour="equipamento-edit-root"]')) {
        return openProtocolo()
    }

    return tourManager
        .navigateAndWait(
            demoRoutes.equipamentoEdit,
            '[data-tour="tab-protocolo"], [data-tour="equipamento-edit-root"]',
            DEMO_PARAM_MQTT_INDEX,
            'demo-parametrizacao'
        )
        .then(() => openProtocolo())
}

const ensureEquipamentoLista = async (tourManager) => {
    if (!demoStore.state.equipamento) demoStore.createEquipamento()

    if (!document.querySelector(LISTA_READY)) {
        await tourManager.navigateAndWait(demoRoutes.equipamentoLista, LISTA_READY, 0, 'demo-parametrizacao')
    }

    document.querySelector('[data-tour="grid-table"] .la-redo-alt')?.closest('button')?.click()

    try {
        await tourManager.waitForElement(getEquipamentoImeiLink, 4000)
    } catch (error) {}

    const link = getEquipamentoImeiLink()
    if (link?.tagName === 'A') {
        link.setAttribute('href', `${demoRoutes.equipamentoEdit}?protocolo=true`)
    }
}

const goToEquipamentoEditFromLista = (tourManager) => {
    if (!demoStore.state.equipamento) demoStore.createEquipamento()

    setDemoTourResume('demo-parametrizacao', demoRoutes.equipamentoEdit, DEMO_PARAM_MQTT_INDEX)

    if (tourManager.isActive()) tourManager.destroy()

    const startMqttStep = () => {
        tourManager.start('demo-parametrizacao', { resumeIndex: DEMO_PARAM_MQTT_INDEX })
    }

    if (tourManager.history) {
        tourManager.history.push(`${demoRoutes.equipamentoEdit}?protocolo=true`)
        setTimeout(startMqttStep, 350)
        return
    }

    window.location.href = `${demoRoutes.equipamentoEdit}?protocolo=true`
}

const goBackToEquipamentoLista = (tourManager) => {
    if (!demoStore.state.equipamento) demoStore.createEquipamento()

    setDemoTourResume('demo-parametrizacao', demoRoutes.equipamentoLista, 0)

    if (tourManager.isActive()) tourManager.destroy()

    const startListaStep = () => {
        tourManager.start('demo-parametrizacao', { resumeIndex: 0 })
    }

    if (tourManager.history) {
        tourManager.history.push(demoRoutes.equipamentoLista)
        setTimeout(startListaStep, 350)
        return
    }

    window.location.href = demoRoutes.equipamentoLista
}

const bindImeiLinkToEdit = (element, tourManager) => {
    const link = element?.closest?.('a') || element?.querySelector?.('a') || element
    if (!link || link._hdvDemoImeiClick) return

    if (link.tagName === 'A') {
        link.setAttribute('href', `${demoRoutes.equipamentoEdit}?protocolo=true`)
    }

    const onClick = (event) => {
        event.preventDefault()
        event.stopPropagation()
        event.stopImmediatePropagation?.()
        goToEquipamentoEditFromLista(tourManager)
    }

    link.addEventListener('click', onClick, true)
    link._hdvDemoImeiClick = onClick
}

const unbindImeiLinkFromEdit = (element) => {
    const link = element?.closest?.('a') || element?.querySelector?.('a') || element
    if (!link?._hdvDemoImeiClick) return
    link.removeEventListener('click', link._hdvDemoImeiClick, true)
    delete link._hdvDemoImeiClick
}

const PARAM_NOVO_READY = '[data-tour="parametrizacao-root"], [data-tour="param-descricao"]'

const openNovoParametro = (tourManager) => {
    enableDemoNovoParametro()
    return tourManager.navigateAndWait(demoRoutes.parametrizacaoNovo, PARAM_NOVO_READY, 0)
}

const advanceToNovoParametro = (tourManager) => {
    if (tourManager._hdvDemoAdvancingNovoParam) return
    tourManager._hdvDemoAdvancingNovoParam = true

    openNovoParametro(tourManager)
        .then(() => tourManager.moveNext())
        .catch(() => tourManager.moveNext())
        .finally(() => {
            tourManager._hdvDemoAdvancingNovoParam = false
        })
}

const bindNovoParametroAsNext = (element, tourManager) => {
    const button = element?.matches?.('[data-tour="param-novo-btn"]')
        ? element
        : document.querySelector('[data-tour="param-novo-btn"]')
    if (!button || button._hdvDemoNovoParamClick) return

    enableDemoNovoParametro()

    const onClick = (event) => {
        event.preventDefault()
        event.stopPropagation()
        event.stopImmediatePropagation?.()
        advanceToNovoParametro(tourManager)
    }

    button.addEventListener('click', onClick, true)
    button.querySelector('a')?.addEventListener('click', onClick, true)
    button._hdvDemoNovoParamClick = onClick
}

const unbindNovoParametroAsNext = (element) => {
    const button = element?.matches?.('[data-tour="param-novo-btn"]')
        ? element
        : document.querySelector('[data-tour="param-novo-btn"]')
    if (!button?._hdvDemoNovoParamClick) return

    const onClick = button._hdvDemoNovoParamClick
    button.removeEventListener('click', onClick, true)
    button.querySelector('a')?.removeEventListener('click', onClick, true)
    delete button._hdvDemoNovoParamClick
}

const enableDemoNovoParametro = () => {
    const button = document.querySelector('[data-tour="param-novo-btn"]')
    const link = button?.querySelector('a')

    button?.classList.remove('disabled')
    if (link) link.style.pointerEvents = 'all'
}

const fillDescricao = () => {
    const descricaoInput = document.querySelector('[data-tour="param-descricao"] input')
    if (descricaoInput) setReactInputValue(descricaoInput, 'Temperatura')
}

const fillIdentificador = () => {
    const identificadorInput = document.querySelector('[data-tour="param-identificador"] input')
    if (identificadorInput) setReactInputValue(identificadorInput, 'temperatura')
}

const fillUnidade = () => {
    const unidade = document.querySelector('[data-tour="param-unidade"] input')
    if (unidade) setReactInputValue(unidade, '°C')
}

const fillTipoDado = () => {
    const tipoDado = document.querySelector('[data-tour="param-tipo-dado"] select')
    if (tipoDado) setReactSelectValue(tipoDado, 'decimal')
}

const fillTipoGrafico = () => {
    const tipoGrafico = document.querySelector('[data-tour="param-tipo-grafico"] select')
    if (tipoGrafico) setReactSelectValue(tipoGrafico, 'linha')
}

const fillDecimal = () => {
    const decimal = document.querySelector(
        '[data-tour="param-decimal"] input, [data-tour="param-decimal"] select'
    )
    if (!decimal) return

    if (decimal.tagName === 'SELECT') {
        setReactSelectValue(decimal, '2')
    } else {
        setReactInputValue(decimal, '2')
    }
}

const fillOperadorEMascara = () => {
    const operador = document.querySelector('[data-tour="param-operador"] select')
    const mascara = document.querySelector('[data-tour="param-mascara"] select')
    if (operador) setReactSelectValue(operador, 'padrao')
    if (mascara) setReactSelectValue(mascara, 'padrao')
}

const fillFormula = () => {
    const formulaEditor = document.querySelector(
        '#formula-input-group .formula-editor, [data-tour="param-formula"] .formula-editor, #formula'
    )
    if (!formulaEditor) return

    if (formulaEditor.isContentEditable) {
        formulaEditor.textContent = '[temperatura]'
        formulaEditor.dispatchEvent(new Event('input', { bubbles: true }))
    } else {
        setReactInputValue(formulaEditor, '[temperatura]')
    }
}

const clickTemperaturaNoPacote = () => {
    const buttons = Array.from(
        document.querySelectorAll('#container-campo-customizado button')
    )
    const temperaturaButton = buttons.find((button) =>
        /24\.5|temperatura/i.test(button.textContent || '')
    )
    if (temperaturaButton) {
        temperaturaButton.click()
        return true
    }
    return false
}

const ensureCampoNoStore = () => {
    if (!demoStore.state.campos.length) {
        demoStore.createCampo({
            equipamento: DEMO_IDS.equipamento,
            identificacao: 'temperatura',
            descricao: 'Temperatura',
            formula: '[temperatura]',
            tipo_grafico: 'linha',
            tipo_dado_saida: 'decimal',
            decimal: '2',
            unidade: '°C',
            mascara: 'padrao',
        })
    }
}

const waitSuccessModal = (tourManager, timeout = 6000) =>
    tourManager.waitForElement(() => {
        const body = document.querySelector('.modal.show .modal-body')
        if (body && /sucesso/i.test(body.textContent || '')) return body
        return null
    }, timeout)

const dismissParamSuccessAndCloseForm = () => {
    const buttons = Array.from(
        document.querySelectorAll('.modal.show .modal-footer button, .modal.show button')
    )
    const continuar = buttons.find((button) =>
        /^continuar$/i.test((button.textContent || '').trim())
    )
    if (continuar) {
        continuar.click()
        return
    }

    document.querySelector('.modal.show .close')?.click()
}

const waitForModalToClose = (tourManager, timeout = 2000) =>
    tourManager.waitForElement(
        () => (!document.querySelector('.modal.show') ? document.body : null),
        timeout
    )

const disablePopoverButtons = ({ next = false, prev = false } = {}) => {
    const nextButton = document.querySelector('.driver-popover-next-btn')
    const prevButton = document.querySelector('.driver-popover-prev-btn')

    if (nextButton && next) {
        nextButton.disabled = true
        nextButton.style.pointerEvents = 'none'
        nextButton.style.opacity = '0.55'
        nextButton.setAttribute('aria-disabled', 'true')
    }

    if (prevButton && prev) {
        prevButton.disabled = true
        prevButton.style.pointerEvents = 'none'
        prevButton.style.opacity = '0.55'
        prevButton.setAttribute('aria-disabled', 'true')
    }
}

const prepareAndSaveParametro = async (tourManager) => {
    fillTipoDado()
    fillTipoGrafico()
    fillUnidade()
    fillDecimal()
    fillOperadorEMascara()
    fillFormula()
    ensureCampoNoStore()

    document.querySelector('[data-tour="param-testar-formula"]')?.click()
    await delay(500)

    const saveButton = document.querySelector('[data-tour="param-salvar"]')
    if (saveButton) {
        saveButton.disabled = false
        withNativeDemoSaveAllowed(saveButton, () => saveButton.click())
        saveButton.style.pointerEvents = 'none'
        saveButton.style.opacity = '0.6'
    }

    try {
        await waitSuccessModal(tourManager)
        await delay(SUCCESS_MODAL_READING_TIME)

        if (document.querySelector('.modal.show')) {
            dismissParamSuccessAndCloseForm()
            await waitForModalToClose(tourManager)
        }

        await tourManager.waitForElement('[data-tour="tab-parametrizacao"], [data-tour="param-novo-btn"], [data-tour="equipamento-edit-root"]', 5000)
        await delay(250)
    } catch (error) {
        ensureCampoNoStore()
        if (tourManager.history) {
            tourManager.history.push(demoRoutes.equipamentoEdit)
            await delay(300)
        }
    }

    ensureCampoNoStore()
}

const saveParametroFromTour = async (tourManager) => {
    if (tourManager._hdvSavingParametro) return
    tourManager._hdvSavingParametro = true
    tourManager._hdvParametroSaved = false
    tourManager.internalNavigation = true

    disablePopoverButtons({ next: true, prev: true })

    const saveButton = document.querySelector('[data-tour="param-salvar"]')
    if (saveButton) {
        saveButton.style.pointerEvents = 'none'
        saveButton.style.opacity = '0.6'
    }

    try {
        await prepareAndSaveParametro(tourManager)
        tourManager._hdvParametroSaved = true
        tourManager.tourPath = tourManager.currentPath()
        if (tourManager.isActive()) {
            tourManager.moveNext()
            tourManager.driverObj?.refresh?.()
        }
    } catch (error) {
        if (saveButton) {
            saveButton.style.pointerEvents = ''
            saveButton.style.opacity = ''
        }
        tourManager.tourPath = tourManager.currentPath()
        if (tourManager.isActive()) tourManager.moveNext()
    } finally {
        tourManager.internalNavigation = false
        setTimeout(() => {
            tourManager._hdvSavingParametro = false
        }, 800)
    }
}

const bindNativeSaveToTour = (button, tourManager) => {
    if (!button) return

    if (button._hdvDemoSaveHandler) {
        button.removeEventListener('click', button._hdvDemoSaveHandler, true)
        delete button._hdvDemoSaveHandler
    }

    unlockDemoSaveButton(button)

    const handler = (event) => {
        if (button._hdvAllowNativeSave) return
        if (tourManager._hdvSavingParametro || tourManager._hdvParametroSaved) {
            event.preventDefault()
            event.stopPropagation()
            event.stopImmediatePropagation?.()
            return
        }
        event.preventDefault()
        event.stopPropagation()
        event.stopImmediatePropagation?.()
        saveParametroFromTour(tourManager)
    }

    button.addEventListener('click', handler, true)
    button._hdvDemoSaveHandler = handler
}

const unbindNativeSaveFromTour = (button) => {
    if (!button?._hdvDemoSaveHandler) return
    button.removeEventListener('click', button._hdvDemoSaveHandler, true)
    delete button._hdvDemoSaveHandler
    lockDemoSaveButton(button)
}

const goToPerfilLista = async (tourManager) => {
    ensureCampoNoStore()
    setDemoTourResume('demo-perfil-cliente', demoRoutes.perfilLista, 0)
    tourManager.internalNavigation = true

    try {
        if (tourManager.isActive()) tourManager.finish('demo-parametrizacao')

        if (tourManager.history) {
            tourManager.history.replace(demoRoutes.perfilLista)
            try {
                await tourManager.waitForElement(
                    '[data-tour="perfil-cliente-novo"], [data-tour="perfil-cliente-lista"], [data-tour="help-perfil-cliente-grid"]',
                    8000
                )
            } catch (error) {}
            await delay(250)
            await tourManager.start('demo-perfil-cliente', { resumeIndex: 0 })
            return
        }

        window.location.href = demoRoutes.perfilLista
    } finally {
        tourManager.internalNavigation = false
    }
}

const buildDemoParametrizacaoTourSteps = (tourManager) => [
    {
        element: getEquipamentoImeiLink,
        ensureReady: () => ensureEquipamentoLista(tourManager),
        popover: {
            title: 'Equipamento salvo',
            description:
                'O equipamento de demonstração foi cadastrado com sucesso e já aparece na lista. Para acessar as abas de Protocolo e Parametrização, é preciso abri-lo em edição.<br><br>Clique no <strong>IMEI</strong> do equipamento ou em <strong>Próximo</strong> para abrir a edição.',
            side: 'bottom',
            align: 'start',
            onNextClick: () => goToEquipamentoEditFromLista(tourManager),
        },
        onHighlighted: (element) => bindImeiLinkToEdit(element, tourManager),
        onDeselected: (element) => unbindImeiLinkFromEdit(element),
    },
    {
        ensureReady: () => ensureEquipamentoEdit(tourManager),
        element: '[data-tour="mqtt-topico-publicar"], [data-tour="mqtt-usuario"], [data-tour="tab-protocolo"]',
        popover: {
            title: 'Tópicos e credenciais MQTT',
            description: 'Os tópicos personalizados, e as credenciais MQTT já estão salvas no equipamento.',
            side: 'bottom',
            align: 'start',
            onPrevClick: () => goBackToEquipamentoLista(tourManager),
            onNextClick: () => {
                clickTab('[data-tour="tab-parametrizacao"]')
                setTimeout(() => tourManager.moveNext(), 150)
            },
        },
        onHighlighted: () => {
            clickTab('[data-tour="tab-protocolo"]')
        },
    },
    {
        element: '[data-tour="tab-parametrizacao"]',
        ensureReady: () => {
            clickTab('[data-tour="tab-parametrizacao"]')
            if (!demoStore.state.equipamento) demoStore.createEquipamento()
            return Promise.resolve()
        },
        skipStage: skipStageConfig('perfilMenu'),
        popover: {
            title: 'Parametrização liberada',
            description: 'Esta aba fica liberada depois do equipamento ser salvo. Vamos cadastrar um primeiro parâmetro para o equipamento.<br><br>Clique em <strong>Pular etapa</strong> para finalizar imediatamente esse cadastro e ir para o próximo.',
            side: 'bottom',
            align: 'start',
        },
    },
    {
        element: '[data-tour="param-novo-btn"]',
        ensureReady: () => {
            clickTab('[data-tour="tab-parametrizacao"]')
            if (!demoStore.state.equipamento) demoStore.createEquipamento()
            enableDemoNovoParametro()
            return tourManager.waitForElement('[data-tour="param-novo-btn"]')
        },
        popover: {
            title: 'Novo parâmetro',
            description: 'Clique em <strong>Próximo</strong> para abrir o formulário de parametrização.',
            side: 'left',
            align: 'start',
            onNextClick: () => advanceToNovoParametro(tourManager),
        },
        onHighlighted: (element) => bindNovoParametroAsNext(element, tourManager),
        onDeselected: (element) => unbindNovoParametroAsNext(element),
    },
    {
        element: '[data-tour="param-descricao"]',
        ensureReady: () => tourManager.waitForElement('[data-tour="param-descricao"]'),
        popover: {
            title: 'Descrição',
            description: 'Dê uma descrição, ou nome de sua escolha para esse parâmetro. É esse nome que vai aparecer nos gráficos e telas do sistema.',
            side: 'bottom',
            align: 'start',
            onNextClick: async () => {
                fillDescricao()
                await delay(120)
                tourManager.moveNext()
            },
        },
    },
    {
        element: '[data-tour="param-identificador"]',
        popover: {
            title: 'Identificador',
            description: 'Um código único para salvar esse parâmetro. Não pode repetir o mesmo identificador em outro parâmetro deste equipamento.<br>Esse campo será preenchido automaticamente ao inserir a descrição.',
            side: 'bottom',
            align: 'start',
            onNextClick: async () => {
                fillIdentificador()
                await delay(120)
                tourManager.moveNext()
            },
        },
    },
    {
        element: '[data-tour="param-formula"], #formula-input-group',
        popover: {
            title: 'Fórmula',
            description: 'Aqui você define como o parâmetro vai calcular seu valor. Pode ser uma conta simples (ex: [ct_pulso.1] * 10), ou até mesmo fórmulas matemáticas complexas de sua escolha. Clique em <strong>Próximo</strong> para abrir o painel com o pacote MQTT.',
            side: 'bottom',
            align: 'start',
            onNextClick: () => {
                openVariaveisPanel(tourManager)
                    .then(() => tourManager.moveNext())
                    .catch(() => tourManager.moveNext())
            },
        },
    },
    {
        element: '#container-campo-customizado',
        ensureReady: () => openVariaveisPanel(tourManager),
        popover: {
            title: 'Pacotes MQTT',
            description: 'Ao clicar no campo da fórmula, este painel exibirá o último pacote de leitura recebido do equipamento. Para inserir um valor na fórmula, clique em uma <span class="formula-badge formula-badge-variavel" style="pointer-events:none;cursor:default">Badge</span>.',
            side: 'left',
            align: 'start',
            onNextClick: async () => {
                if (!clickTemperaturaNoPacote()) {
                    fillFormula()
                }
                await delay(150)
                tourManager.moveNext()
            },
        },
    },
    {
        element: '[data-tour="param-btn-formulas"]',
        popover: {
            title: 'Fórmulas prontas',
            description: 'Caso não tenha uma fórmula, este botão abre modelos prontos. Se o modal for aberto, o guia será minimizado temporariamente.',
            side: 'bottom',
            align: 'end',
        },
        onHighlighted: (element) => {
            bindMinimizeOnClick(element, () => tourManager.minimize())
        },
        onDeselected: (element) => {
            unbindMinimizeOnClick(element)
        },
    },
    {
        element: '[data-tour="param-tipo-dado"]',
        popover: {
            title: 'Tipo de dado',
            description: 'Escolha o tipo de resultado que essa fórmula vai gerar: número decimal, número inteiro, verdadeiro/falso ou texto.',
            side: 'bottom',
            align: 'start',
            onNextClick: async () => {
                fillTipoDado()
                await delay(120)
                tourManager.moveNext()
            },
        },
    },
    {
        element: '[data-tour="param-tipo-grafico"]',
        popover: {
            title: 'Tipo de gráfico',
            description:
                'Escolha como este parâmetro será exibido nos gráficos:<br><br>' +
                '• <strong>Linha</strong>: para valores que variam.<br>' +
                '• <strong>Barra</strong>: para valores acumulados.',
            side: 'bottom',
            align: 'start',
            onNextClick: async () => {
                fillTipoGrafico()
                await delay(120)
                tourManager.moveNext()
            },
        },
    },
    {
        element: '[data-tour="param-unidade"]',
        popover: {
            title: 'Unidade de medida',
            description: 'Aqui é definida a unidade de medida do parâmetro, como m³, °C, metros ou horas.',
            side: 'bottom',
            align: 'start',
            onNextClick: async () => {
                fillUnidade()
                await delay(120)
                tourManager.moveNext()
            },
        },
    },
    {
        element: '[data-tour="param-decimal"]',
        popover: {
            title: 'Casas decimais',
            description: 'Escolha quantas casas decimais devem aparecer no valor exibido.',
            side: 'bottom',
            align: 'start',
            onNextClick: async () => {
                fillDecimal()
                await delay(120)
                tourManager.moveNext()
            },
        },
    },
    {
        element: '[data-tour="param-operador"]',
        popover: {
            title: 'Operador especial',
            description:
                'Define como o resultado da fórmula será calculado:<br><br>' +
                '• <strong>Padrão</strong>: executa a fórmula normalmente.<br>' +
                '• <strong>Incremental</strong>: acumula os valores ao longo do tempo.<br>' +
                '• <strong>Volume do dia/semana/mês</strong>: acumula os valores e reinicia a contagem automaticamente no início de cada período.',
            side: 'bottom',
            align: 'start',
        },
    },
    {
        element: '[data-tour="param-mascara"]',
        popover: {
            title: 'Máscara',
            description:
                'A máscara formata a exibição do valor. Para horas em número bruto, o formato hh:mm:ss é aplicado automaticamente.',
            side: 'bottom',
            align: 'start',
        },
    },
    {
        element: '[data-tour="param-icone"]',
        popover: {
            title: 'Ícone',
            description: 'Escolha um ícone para representar esse parâmetro nas telas do sistema.',
            side: 'top',
            align: 'start',
        },
    },
    {
        element: '[data-tour="param-testar-formula"]',
        popover: {
            title: 'Testar fórmula',
            description: 'Utilize esse botão para testar o resultado da fórmula. Se houver algum erro, o sistema vai avisar que a fórmula é inválida.<br>Em <strong>Próximo</strong> testamos automaticamente.',
            side: 'top',
            align: 'start',
            onNextClick: async () => {
                fillTipoDado()
                fillTipoGrafico()
                fillUnidade()
                fillDecimal()
                if (!document.querySelector('#formula-input-group .formula-editor')?.textContent?.trim()) {
                    fillFormula()
                }
                document.querySelector('[data-tour="param-testar-formula"]')?.click()
                await delay(400)
                tourManager.moveNext()
            },
        },
    },
    {
        element: '[data-tour="param-salvar"]',
        popover: {
            title: 'Salvar',
            description: 'Este botão salva o parâmetro. Ele é liberado após clicar em <strong>Testar fórmula</strong> com uma fórmula válida. Clique em <strong>Salvar</strong> para prosseguir, aguardamos a confirmação antes de seguir.',
            side: 'top',
            align: 'start',
            nextBtnText: 'Salvar',
            onNextClick: () => saveParametroFromTour(tourManager),
        },
        onHighlighted: (element) => {
            if (tourManager._hdvParametroSaved || tourManager._hdvSavingParametro) {
                disablePopoverButtons({ next: true, prev: true })
                return
            }
            bindNativeSaveToTour(element, tourManager)
        },
        onDeselected: (element) => unbindNativeSaveFromTour(element),
    },
    {
        popover: {
            title: 'Parametrização concluída',
            description: 'Parâmetro gravado com sucesso. Em <strong>Ir para perfis</strong> abrimos a lista de perfis de cliente para continuar a demonstração.',
            side: 'over',
            align: 'center',
            nextBtnText: 'Ir para perfis',
            disableButtons: ['previous'],
            onNextClick: () => {
                goToPerfilLista(tourManager)
            },
        },
        onHighlighted: () => {
            disablePopoverButtons({ prev: true })
        },
    },
    {
        popover: {
            title: 'Continuando…',
            description: 'Abrindo a lista de perfis de cliente…',
            showButtons: ['close'],
            disableButtons: ['previous'],
        },
    },
]

export default buildDemoParametrizacaoTourSteps
